<script lang="ts">
	import { push } from 'svelte-spa-router';
	// Stores
	import { user_settings } from 'src/stores/account/user';
	// Services
	import { postReq } from 'src/services/http';
	import { format } from 'svelte-i18n';
	// Design system components
	import { CircleAvatar, ToggleV2, toast, Text } from '@Shelly/shelly-design-system';
	//Helpers
	import { disableAllNetMetering } from 'src/helpers/statistics/core/net_metering/chart_net_metering_support_helpers';

	import { save_home } from 'src/helpers/home_helpers';
	import { deletePvSetup } from 'src/components/Consumption/PVSetupEnergy/pv_setup_helper';
	import { ENERGY_PV_SETTINGS_HREF } from 'src/components/EnergyMonitoring/helpers/energy_widget_state_helpers';
	import { experimental_feature_feedback_reminders } from 'src/stores/features/experimental-reminders';
	import {
		CLONE_SETTINGS_DISCORD_LINK,
		FIVE_DAYS_MS,
		MATH_IN_SCENES_DISCORD_LINK,
		NET_METERING_DISCORD_LINK,
		PV_SETUP_DISCORD_LINK,
		SCRIPT_SCENES_DISCORD_LINK,
	} from 'src/constants';

	// dedicated Components
	import CopySettingsOnboardingWizard from './ExperimentalFeatures/CopySettingsOnboardingWizardV2.svelte';

	// shared components
	import NetMeteringInfoModal from 'src/components/Consumption/NetMeteringInfoModalV2.svelte';
	import PVSetupConfig from 'src/components/Consumption/PVSetupEnergy/PVSetupConfigV2.svelte';
	import { openExternalLink } from 'src/helpers/utilities';
	import { onMount, onDestroy } from 'svelte';
	import { get } from 'svelte/store';

	let showCloneSettingsOnboarding = false;
	let showScriptScenesOnboarding = false;

	const reminderTimers = new Map<string, number>();

	const cancelFeedbackReminder = (featureId: string) => {
		if (typeof window === 'undefined') {
			return;
		}
		const timerId = reminderTimers.get(featureId);
		if (timerId) {
			window.clearTimeout(timerId);
			reminderTimers.delete(featureId);
		}
	};

	const scheduleFeedbackReminder = (
		featureId: string,
		featureName: string,
		feedbackLink: string,
		enabledAt: number,
	) => {
		cancelFeedbackReminder(featureId);
		if (typeof window === 'undefined') {
			return;
		}
		if (!feedbackLink) {
			experimental_feature_feedback_reminders.clearReminder(featureId);
			return;
		}
		const now = Date.now();
		const elapsed = now - enabledAt;
		const remaining = Math.max(FIVE_DAYS_MS - elapsed, 0);

		const handleReminder = () => {
			toast.open({
				type: 'info',
				title: $format('experimental_features_feedback_reminder_title', {
					default: 'Share your feedback',
				}),
				text: $format('experimental_features_feedback_reminder_text', {
					default:
						'It has been a few days since you enabled {name}. Join the Discord discussion to share your thoughts: {link}',
					values: { name: featureName, link: feedbackLink },
				}),
				duration: 7000,
			});
			reminderTimers.delete(featureId);
			experimental_feature_feedback_reminders.clearReminder(featureId);
		};

		if (remaining === 0) {
			handleReminder();
			return;
		}

		const timerId = window.setTimeout(handleReminder, remaining);
		reminderTimers.set(featureId, timerId);
	};

	onMount(() => {
		if (typeof window === 'undefined') {
			return;
		}
		const storedReminders = get(experimental_feature_feedback_reminders);
		const settings = get(user_settings);
		Object.entries(storedReminders).forEach(([featureId, enabledAt]) => {
			const feature = experimentalFeatureOptions.find((f) => f.id === featureId);
			if (!feature) {
				experimental_feature_feedback_reminders.clearReminder(featureId);
				return;
			}
			const isFeatureEnabled =
				settings?.experimental_features?.[featureId]?.enabled === 1 ||
				selectedFeatures.has(featureId);
			if (!isFeatureEnabled || !feature.feedbackLink) {
				experimental_feature_feedback_reminders.clearReminder(featureId);
				return;
			}
			scheduleFeedbackReminder(featureId, feature.name, feature.feedbackLink, enabledAt);
		});
	});

	onDestroy(() => {
		if (typeof window === 'undefined') {
			return;
		}
		reminderTimers.forEach((timerId) => window.clearTimeout(timerId));
		reminderTimers.clear();
	});

	/**
	 * Future features can be added here:
	 * {
	 *     id: 'new_feature',
	 *     name: $format('feature_new_feature_name', {default: 'New Feature Name'}),
	 *     description: $format('feature_new_feature_description', {default: 'Description of new feature'})
	 * }
	 */
	$: experimentalFeatureOptions = [
		{
			id: 'inf2',
			name: $format('feature_inf2_name', {
				default: 'Script Scenes',
			}),
			description: $format('feature_inf2_description', {
				default: 'Convert and edit scenes using our new editor with Javascript.',
			}),
			feedbackLink: SCRIPT_SCENES_DISCORD_LINK,
			icon: 'fa-light fa-code',
		},
		{
			id: 'mis',
			name: $format('feature_mis_name', { default: 'Math in Scenes' }),
			description: $format('feature_mis_description', {
				default: 'Enable mathematical expressions in your scenes',
			}),
			feedbackLink: MATH_IN_SCENES_DISCORD_LINK,
			icon: 'fa-light fa-square-root-variable',
		},
		{
			id: 'cs',
			name: $format('feature_cd_name', {
				default: 'Clone Device Settings Configuration',
			}),
			description: $format('feature_cd_description', {
				default: 'Clone a device settings configuration or pre-saved template to another device',
			}),
			feedbackLink: CLONE_SETTINGS_DISCORD_LINK,
			icon: 'fa-light fa-copy',
		},
		{
			id: 'nm',
			name: $format('feature_nm_name', {
				default: 'Net Metering Support',
			}),
			description: $format('feature_nm_description', {
				default:
					'Enable net metering calculation and visualization in energy statistics for compatible Energy Meter devices (e.g. Shelly EM Gen3, Shelly Pro 3EM).',
			}),
			feedbackLink: NET_METERING_DISCORD_LINK,
			icon: 'fa-light fa-gauge',
		},
		{
			id: 'pvs',
			name: $format('feature_pvs_name', {
				default: 'PV Setup for Energy Meters',
			}),
			description: $format('feature_pvs_description', {
				default:
					'Enable PV Setup in the Energy Meter settings to configure and monitor your photovoltaic system.',
			}),
			feedbackLink: PV_SETUP_DISCORD_LINK,
			icon: 'fa-light fa-solar-panel',
		},
		// Future features can be added here:
		// {
		//     id: 'new_feature',
		//     name: $format('feature_new_feature_name', {default: 'New Feature Name'}),
		//     description: $format('feature_new_feature_description', {default: 'Description of new feature'})
		// }
	].filter(Boolean);

	// let showModal = false;
	let showPVSetupModal = false;
	let showNetMeteringModal = false;
	let selectedFeatures = new Set();

	// Initialize selected features based on user settings
	$: {
		if ($user_settings?.experimental_features) {
			selectedFeatures = new Set();

			// Check all possible features
			experimentalFeatureOptions.forEach((feature) => {
				if ($user_settings.experimental_features[feature.id]?.enabled === 1) {
					selectedFeatures.add(feature.id);
				}
			});
		}
	}

	const toggleFeatureSelection = async (featureId, event) => {
		if (event) {
			event.stopPropagation();
		}

		const willBeEnabled = !selectedFeatures.has(featureId);

		if (willBeEnabled && featureId === 'cs') {
			showCloneSettingsOnboarding = true;
		}

		if (willBeEnabled && featureId === 'inf2') {
			showScriptScenesOnboarding = true;
		}

		// Find the feature details
		const feature = experimentalFeatureOptions.find((f) => f.id === featureId);
		if (!feature) return;

		if (willBeEnabled) {
			selectedFeatures.add(featureId);
		} else {
			selectedFeatures.delete(featureId);
		}

		try {
			let res = await postReq('/user/experimental_features', {
				set: featureId,
				val: willBeEnabled ? 1 : 0,
			});

			if (!res.isok) {
				if (willBeEnabled) {
					selectedFeatures.delete(featureId);
				} else {
					selectedFeatures.add(featureId);
				}

				toast.open({
					type: 'danger',
					title: $format('error_global', { default: 'Something went wrong!' }),
				});
				return;
			}

			// Update store for the specific feature
			user_settings.update((settings) => {
				if (!settings.experimental_features) {
					settings.experimental_features = {};
				}

				if (!settings.experimental_features[featureId]) {
					settings.experimental_features[featureId] = {};
				}

				settings.experimental_features[featureId].enabled = willBeEnabled ? 1 : 0;
				return settings;
			});

			// Disable all Net Metering functionality when the experimental feature is turned off,
			// or display the information modal when it is enabled.
			if (featureId === 'nm') {
				if (willBeEnabled === false) {
					disableAllNetMetering();
				} else {
					showNetMeteringModal = true;
					// showModal = false;
				}
			} else if (featureId === 'pvs') {
				if (willBeEnabled === false) {
					await deletePvSetup();
				} else {
					showPVSetupModal = true;
				}
			}

			// Only show toast for features without onboarding modals when enabling
			const hasOnboarding = ['cs', 'nm', 'pvs'].includes(featureId);
			if (willBeEnabled && !hasOnboarding) {
				toast.open({
					type: 'warning',
					title: $format('experimental_features_enabled_title', {
						default: '{name} Enabled',
						values: { name: feature.name },
					}),
					text: $format('experimental_features_enabled_text', {
						default:
							'You have enabled {name}. This feature may not work correctly with all apps.',
						values: { name: feature.name },
					}),
					duration: 5000,
				});
			}
		} catch (error) {
			console.error('Error toggling experimental feature:', error);
			if (willBeEnabled) {
				selectedFeatures.delete(featureId);
			} else {
				selectedFeatures.add(featureId);
			}

			toast.open({
				type: 'danger',
				title: $format('error_global', { default: 'Something went wrong!' }),
			});
		}
	};
</script>

<div class="mt-s d-flex flex-direction-column gap-m">
	{#each experimentalFeatureOptions as feature, i (feature.id)}
		<div class="d-flex justify-content-between align-items-center gap-s experimental-feature-row">
			<div class="d-flex align-items-center flex-grow-1 min-width-0">
				<CircleAvatar icon={feature.icon} size="m" class="mr-m flex-shrink-0" />
				<div class="min-width-0">
					<Text styleType="body-medium" class="mb-xs">{feature.name}</Text>
					<Text styleType="body-small" class="text-color-muted">{feature.description}</Text>
					{#if feature.feedbackLink}
						<Text
							styleType="body-small"
							class="mt-xs link-styles text-small d-inline-flex align-items-center text-primary"
							on:click={() => openExternalLink(feature.feedbackLink)}
							target="_blank"
							rel="noopener noreferrer"
						>
							{$format('experimental_features_feedback_discord', {
								default: 'Discuss this feature on Discord',
								values: { name: feature.name },
							})}
						</Text>
					{/if}
				</div>
			</div>
			<div class="d-flex align-items-center flex-shrink-0">
				<ToggleV2
					size="m"
					class="flex-shrink-0 pt-0 pb-0"
					checked={selectedFeatures.has(feature.id)}
					on:click={(event) => toggleFeatureSelection(feature.id, event)}
					aria-label={$format(`toggle_${feature.name}`, {
						default: 'Toggle {name}',
						values: { name: feature.name },
					})}
					id={feature.id}
				/>
			</div>
		</div>
		{#if i < experimentalFeatureOptions.length - 1}
			<hr class="hr-color-2" />
		{/if}
	{/each}
</div>

<NetMeteringInfoModal bind:showModal={showNetMeteringModal} />

{#if showCloneSettingsOnboarding}
	<CopySettingsOnboardingWizard bind:showModal={showCloneSettingsOnboarding} />
{/if}

<PVSetupConfig
	type="Parallel"
	bind:showModal={showPVSetupModal}
	on:save={async (e) => {
		await save_home('net_metering_type', e.detail);
		push(ENERGY_PV_SETTINGS_HREF);
	}}
/>
